import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProfileField } from "@/hooks/useHealthProfile";
import MLoader from "@/components/ui/atoms/MLoader";
import MDialog from "@/components/ui/atoms/MDialog";
import { getMemberInfo } from "@/hooks/useMembers";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { ListItem } from "@/components/ui/atoms/MListItem";
import { IconCircleX, IconEdit } from "@tabler/icons-react";
import FormHeader from "@/components/ui/organisms/MHeader";
import { MedicalDetailType } from "@/types/health-profile";
import { useTranslation } from "react-i18next";
import { ISurgery } from "@/types/surgeries";
import { formattedYear } from "@/utils/date-utils";
import { useNavigationSource } from '@/hooks/useNavigationSource';

const Surgeries: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  // Navigation source
  const { navigateBackIfExists } = useNavigationSource();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;
  const {
    fieldValue: surgeries = [],
    saveField: saveSurgeries,
    loading: surgeriesLoading,
    error: surgeriesError,
  } = useProfileField<ISurgery[]>(memberId, MedicalDetailType.SURGERIES, []);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [surgeryToDelete, setSurgeryToDelete] = useState<ISurgery | null>(null);

  if (surgeriesLoading) {
    return <MLoader />;
  }

  if (surgeriesError) {
    return (
      <MDialog
        isOpen={true}
        title={t("surgeries.couldNotLoad")}
        description={t("surgeries.couldNotLoadDescription")}
        primaryCtaText={t("common.retry")}
        onPrimaryCta={() => window.location.reload()}
        onClose={() => window.location.reload()}
      />
    );
  }

  const handleDelete = async () => {
    if (!surgeryToDelete) return;
    setDeleteLoading(true);
    try {
      const updatedSurgeries = surgeries.filter(
        (s) =>
          !(s.name === surgeryToDelete.name && s.date === surgeryToDelete.date)
      );
      await saveSurgeries(updatedSurgeries);
      setShowDeleteModal(false);
      setSurgeryToDelete(null);
      if (updatedSurgeries.length === 0) {
        // Navigate back to source if exists, otherwise go to health profile
        const didNavigate = navigateBackIfExists();
        if (!didNavigate) {
          navigate("/profile/health-profile");
        }
        return;
      }
    } catch (error) {
      showToast({
        message: t("errors.toast"),
        variant: "error",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEdit = (surgery: ISurgery, idx: number) => {
    navigate(
      '/profile/health-profile/surgeries/SurgeriesForm', {
        state: {
          surgery,
          idx
        },
      }
    );
  };

  const sortedSurgeries = surgeries.sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return dateB.getTime() - dateA.getTime();
  });

  return (
    <div className="profile-page-root">
      <FormHeader
        module="Surgeries"
        memberInfo={memberInfo}
        onAdd={() =>
          navigate("/profile/health-profile/surgeries/SurgeriesForm?mode=add")
        }
        showGradient
        onBack={() => {
          const didNavigate = navigateBackIfExists();
          if (!didNavigate) {
            navigate("/profile/health-profile");
          }
        }}
      />
      <div className="profile-flex-col-gap">
        {surgeries.length > 0 &&
          sortedSurgeries.map((surgery, idx) => (
            <div
              key={surgery.name + surgery.date + idx}
              style={{ position: "relative" }}
            >
              <ListItem
                usage="crud"
                type="2-line"
                variant="purple"
                title={surgery.name}
                secondaryText={
                  surgery.date
                    ? formattedYear(surgery.date)
                    : t("surgeries.yearNotAnswered")
                }
                actions={[
                  {
                    id: "edit",
                    label: t("common.edit"),
                    icon: <IconEdit size={20} />,
                    onClick: () => handleEdit(surgery, idx),
                  },
                  {
                    id: "delete",
                    label: t("common.delete"),
                    icon: <IconCircleX size={20} />,
                    onClick: () => {
                      setSurgeryToDelete(surgery);
                      setShowDeleteModal(true);
                    },
                    className: "delete",
                  },
                ]}
              />
            </div>
          ))}
      </div>
      {showDeleteModal && surgeryToDelete && (
        <MDialog
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSurgeryToDelete(null);
          }}
          title={t("surgeries.deleteThisRecord")}
          description={t(
            `surgeries.deleteThisRecordDescription${
              memberInfo.onBehalf ? "_on_behalf" : ""
            }`
          )}
          primaryCtaText={t("common.delete")}
          onPrimaryCta={handleDelete}
          secondaryCtaText="Cancel"
          onSecondaryCta={() => {
            setShowDeleteModal(false);
            setSurgeryToDelete(null);
          }}
          isPrimaryCtaLoading={deleteLoading}
          isPrimaryCtaDisabled={deleteLoading}
        />
      )}
    </div>
  );
};

export default Surgeries;
